import { useCallback, useEffect, useMemo, useState } from 'react';
import { createArticle, fetchArticles, setArticleFavorite, setArticleLike } from '@domain/plaza/api';
import type { Article, ArticleLocation, ArticleVideo } from '@domain/plaza/types';
import { uploadMedia } from '@domain/media/api';
import { MIN_PLAZA_REFRESH_MS } from '@shared/config/runtime';
import { ARTICLE_MAX_IMAGES } from '@shared/media/uploadPolicy';
import { processImage } from '@shared/media/processing/imageProcessor';
import { processArticleVideo } from '@shared/media/processing/articleVideoProcessor';
import {
  fallbackImageMeta,
  getErrorMessage,
  nextLocalMessageId,
  readBrowserImageMeta,
  type ImageMessageMeta,
} from '@shared/utils/ui';

export type PlazaRefreshSource = 'initial' | 'button' | 'pull' | 'post';

type PostComposerImage = {
  id: string;
  file: File;
  previewUrl: string;
  meta: ImageMessageMeta;
};

type PostComposerVideo = {
  id: string;
  file: File;
  previewUrl: string;
};

function mergeArticlesById(existing: Article[], incoming: Article[]) {
  const merged = new Map<string, Article>();
  for (const article of existing) {
    merged.set(article.id, article);
  }
  for (const article of incoming) {
    merged.set(article.id, article);
  }
  return Array.from(merged.values()).sort((left, right) => right.created - left.created);
}

type UsePlazaFeedOptions = {
  activeTab: 'chats' | 'contacts' | 'plaza' | 'settings';
  apiBase: string;
  isAuthenticated: boolean;
  pushLog: (msg: string, kind?: 'info' | 'in' | 'out' | 'error' | 'success' | 'warn') => void;
};

export function usePlazaFeed({
  activeTab,
  apiBase,
  isAuthenticated,
  pushLog,
}: UsePlazaFeedOptions) {
  const [plazaArticles, setPlazaArticles] = useState<Article[]>([]);
  const [plazaPage, setPlazaPage] = useState(1);
  const [plazaHasMore, setPlazaHasMore] = useState(true);
  const [plazaLoadState, setPlazaLoadState] = useState<'idle' | 'loading' | 'loadingMore' | 'error'>('idle');
  const [plazaError, setPlazaError] = useState<string | null>(null);
  const [, setIsPlazaAtTop] = useState(true);
  const [plazaRefreshSource, setPlazaRefreshSource] = useState<PlazaRefreshSource>('initial');
  const [hasInitialLoaded, setHasInitialLoaded] = useState(false);
  const [isPostModalOpen, setIsPostModalOpen] = useState(false);
  const [postTitle, setPostTitle] = useState('');
  const [postContent, setPostContent] = useState('');
  const [postTags, setPostTags] = useState('');
  const [postSubmitting, setPostSubmitting] = useState(false);
  const [postSubmitStatus, setPostSubmitStatus] = useState<string | null>(null);
  const [postSubmitError, setPostSubmitError] = useState<string | null>(null);
  const [postImages, setPostImages] = useState<PostComposerImage[]>([]);
  const [postVideo, setPostVideo] = useState<PostComposerVideo | null>(null);
  const [postLocation, setPostLocation] = useState<ArticleLocation | null>(null);
  const [postLocationPending, setPostLocationPending] = useState(false);

  const resetPostComposer = useCallback(() => {
    setPostTitle('');
    setPostContent('');
    setPostTags('');
    setPostSubmitting(false);
    setPostSubmitStatus(null);
    setPostSubmitError(null);
    setPostLocation(null);
    setPostLocationPending(false);
    setPostImages((prev) => {
      for (const image of prev) {
        URL.revokeObjectURL(image.previewUrl);
      }
      return [];
    });
    setPostVideo((prev) => {
      if (prev) URL.revokeObjectURL(prev.previewUrl);
      return null;
    });
  }, []);

  const closePostComposer = useCallback(() => {
    setIsPostModalOpen(false);
    resetPostComposer();
  }, [resetPostComposer]);

  const canSubmitPost = useMemo(
    () => Boolean(postTitle.trim() && (postContent.trim() || postImages.length > 0 || postVideo) && !postSubmitting),
    [postContent, postImages.length, postSubmitting, postTitle, postVideo],
  );

  const loadPlazaArticles = useCallback(async (refresh = false, source: PlazaRefreshSource = 'button') => {
    if (plazaLoadState === 'loading' || plazaLoadState === 'loadingMore') return;
    const nextPage = refresh ? 1 : plazaPage;
    const requestStartedAt = Date.now();
    if (refresh) {
      setPlazaRefreshSource(source);
    }
    setPlazaLoadState(refresh ? 'loading' : 'loadingMore');
    setPlazaError(null);
    try {
      const { articles, pagination } = await fetchArticles(apiBase, nextPage);
      if (refresh) {
        const elapsed = Date.now() - requestStartedAt;
        if (elapsed < MIN_PLAZA_REFRESH_MS) {
          await new Promise((resolve) => window.setTimeout(resolve, MIN_PLAZA_REFRESH_MS - elapsed));
        }
      }
      setPlazaArticles((prev) => (
        refresh
          ? [...articles].sort((left, right) => right.created - left.created)
          : mergeArticlesById(prev, articles)
      ));
      setPlazaPage(nextPage + 1);
      setPlazaHasMore((pagination.page * pagination.limit) < pagination.total);
      setHasInitialLoaded(true);
    } catch (error) {
      if (refresh) {
        const elapsed = Date.now() - requestStartedAt;
        if (elapsed < MIN_PLAZA_REFRESH_MS) {
          await new Promise((resolve) => window.setTimeout(resolve, MIN_PLAZA_REFRESH_MS - elapsed));
        }
      }
      setPlazaError(getErrorMessage(error));
    } finally {
      setPlazaLoadState('idle');
    }
  }, [apiBase, plazaLoadState, plazaPage]);

  useEffect(() => {
    if (activeTab === 'plaza' && !hasInitialLoaded && plazaLoadState === 'idle' && isAuthenticated) {
      void loadPlazaArticles(true, 'initial');
    }
  }, [activeTab, isAuthenticated, loadPlazaArticles, hasInitialLoaded, plazaLoadState]);

  useEffect(() => {
    if (!isAuthenticated) {
      setPlazaArticles([]);
      setPlazaPage(1);
      setPlazaHasMore(true);
      setHasInitialLoaded(false);
    }
  }, [isAuthenticated]);

  const handleSubmitPost = useCallback(async () => {
    if (!postTitle.trim() || (!postContent.trim() && postImages.length === 0 && !postVideo) || postSubmitting) return;
    setPostSubmitting(true);
    setPostSubmitError(null);
    try {
      setPostSubmitStatus(postImages.length > 0 ? '正在压缩图片…' : null);
      const uploadedImages = await Promise.all(postImages.map(async (image) => {
        const processed = await processImage(image.file, { use: 'article', quality: 'standard' });
        return {
          media: await uploadMedia(apiBase, processed.file, {
            domain: 'article',
            kind: 'image',
            metadata: {
              ...processed.metadata,
              policyVersion: processed.policyVersion,
              quality: 'standard',
              sourceSizeBytes: processed.source.sizeBytes,
              outputSizeBytes: processed.metadata.sizeBytes,
            },
          }),
          meta: { ...image.meta, ...processed.metadata },
        };
      }));
      let articleVideo: ArticleVideo | null = null;
      if (postVideo) {
        setPostSubmitStatus('正在压缩视频，首次使用需要加载处理组件…');
        const processed = await processArticleVideo(postVideo.file);
        console.info('[media] article video processed', {
          sourceBytes: processed.source.sizeBytes,
          outputBytes: processed.metadata.sizeBytes,
          width: processed.metadata.width,
          height: processed.metadata.height,
          durationMs: processed.metadata.durationMs,
        });
        setPostSubmitStatus('视频压缩完成，正在上传…');
        const uploadedVideo = await uploadMedia(apiBase, processed.file, {
          domain: 'article',
          kind: 'video',
          metadata: {
            ...processed.metadata,
            policyVersion: processed.policyVersion,
            quality: 'standard',
            sourceSizeBytes: processed.source.sizeBytes,
            outputSizeBytes: processed.metadata.sizeBytes,
          },
        });
        articleVideo = {
          mediaId: uploadedVideo.id,
          provider: 's3',
          url: uploadedVideo.url,
          meta: {
            fileName: processed.metadata.fileName,
            size: processed.metadata.sizeBytes,
            mimeType: processed.metadata.mimeType,
            width: processed.metadata.width,
            height: processed.metadata.height,
            durationMs: processed.metadata.durationMs,
          },
        };
      }
      setPostSubmitStatus('正在发布动态…');
      await createArticle(apiBase, {
        title: postTitle.trim(),
        body: postContent.trim(),
        media: {
          images: uploadedImages.map((image, index) => ({
            mediaId: image.media.id,
            provider: 's3',
            url: image.media.url,
            meta: image.meta,
            sortOrder: index,
          })),
          video: articleVideo,
        },
        tags: postTags.split(/[\s,，#]+/).map((tag) => tag.trim()).filter(Boolean).slice(0, 10),
        location: postLocation,
      });
      setIsPostModalOpen(false);
      resetPostComposer();
      void loadPlazaArticles(true, 'post');
    } catch (error) {
      const message = getErrorMessage(error);
      setPostSubmitError(message);
      pushLog(`发布动态失败: ${message}`, 'error');
    } finally {
      setPostSubmitting(false);
      setPostSubmitStatus(null);
    }
  }, [apiBase, loadPlazaArticles, postContent, postImages, postLocation, postSubmitting, postTags, postTitle, postVideo, pushLog, resetPostComposer]);

  const handleUseCurrentLocation = useCallback(() => {
    if (!navigator.geolocation || postLocationPending) {
      if (!navigator.geolocation) pushLog('当前浏览器不支持定位', 'error');
      return;
    }
    setPostLocationPending(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setPostLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          label: '当前位置附近',
          precision: 'approximate',
        });
        setPostLocationPending(false);
      },
      (error) => {
        pushLog(`无法获取当前位置: ${error.message}`, 'error');
        setPostLocationPending(false);
      },
      { enableHighAccuracy: false, timeout: 10_000, maximumAge: 300_000 },
    );
  }, [postLocationPending, pushLog]);

  const handleAddPostImages = useCallback((files: File[]) => {
    if (files.length === 0) {
      return;
    }

    void (async () => {
      const imageEntries = await Promise.all(files.map(async (file) => ({
        id: nextLocalMessageId(),
        file,
        previewUrl: URL.createObjectURL(file),
        meta: await readBrowserImageMeta(file).catch(() => fallbackImageMeta(file)),
      })));

      setPostImages((prev) => {
        const remaining = Math.max(0, ARTICLE_MAX_IMAGES - prev.length);
        const nextImages = imageEntries.slice(0, remaining);
        for (const droppedImage of imageEntries.slice(remaining)) {
          URL.revokeObjectURL(droppedImage.previewUrl);
        }
        return [...prev, ...nextImages];
      });
    })();
  }, []);

  const handleRemovePostImage = useCallback((imageId: string) => {
    setPostImages((prev) => {
      const target = prev.find((image) => image.id === imageId);
      if (target) {
        URL.revokeObjectURL(target.previewUrl);
      }
      return prev.filter((image) => image.id !== imageId);
    });
  }, []);

  const handleSelectPostVideo = useCallback((files: File[]) => {
    const file = files[0];
    if (!file) return;
    setPostSubmitError(null);
    setPostVideo((prev) => {
      if (prev) URL.revokeObjectURL(prev.previewUrl);
      return {
        id: nextLocalMessageId(),
        file,
        previewUrl: URL.createObjectURL(file),
      };
    });
  }, []);

  const handleRemovePostVideo = useCallback(() => {
    setPostVideo((prev) => {
      if (prev) URL.revokeObjectURL(prev.previewUrl);
      return null;
    });
  }, []);

  const replaceArticle = useCallback((updated: Article) => {
    setPlazaArticles((prev) => prev.map((article) => article.id === updated.id ? updated : article));
  }, []);

  const handleToggleArticleLike = useCallback(async (article: Article) => {
    try {
      replaceArticle(await setArticleLike(apiBase, article.id, !article.viewer.liked));
    } catch (error) {
      pushLog(`点赞失败: ${getErrorMessage(error)}`, 'error');
    }
  }, [apiBase, pushLog, replaceArticle]);

  const handleToggleArticleFavorite = useCallback(async (article: Article) => {
    try {
      replaceArticle(await setArticleFavorite(apiBase, article.id, !article.viewer.favorited));
    } catch (error) {
      pushLog(`收藏失败: ${getErrorMessage(error)}`, 'error');
    }
  }, [apiBase, pushLog, replaceArticle]);

  return {
    plazaArticles,
    plazaHasMore,
    plazaLoadState,
    plazaError,
    setIsPlazaAtTop,
    plazaRefreshSource,
    isPostModalOpen,
    setIsPostModalOpen,
    postTitle,
    setPostTitle,
    postContent,
    setPostContent,
    postTags,
    setPostTags,
    postSubmitting,
    postSubmitStatus,
    postSubmitError,
    postImages,
    postVideo,
    postLocation,
    postLocationPending,
    canSubmitPost,
    closePostComposer,
    loadPlazaArticles,
    handleSubmitPost,
    handleAddPostImages,
    handleRemovePostImage,
    handleSelectPostVideo,
    handleRemovePostVideo,
    handleUseCurrentLocation,
    removePostLocation: () => setPostLocation(null),
    handleToggleArticleLike,
    handleToggleArticleFavorite,
  };
}
